import { Container, Link, List, Message, Stack, Text } from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { Switch } from "components/Switch";
import { STATIC_RESOURCES } from "constants/contacts";
import { getRegional } from "library/getRegional";
import { useCardTempLock } from "pages/TemporaryLockUnlockCard/hooks";
import { LockUnlockCardImage } from "./LockUnlockCardImage";
import { LockUnlockUnavailable } from "./LockUnlockUnavailable";

interface Props {
  token: string;
  imageFilename: string;
  cardName: string;
  cardEndsWith: string;
}

const StateMap = {
  LOCKED: true,
  UNLOCKED: false,
};

const { tncPdfAu, tncPdfNz } = STATIC_RESOURCES;

export const LockUnlockCard = ({
  token,
  imageFilename,
  cardName,
  cardEndsWith,
}: Props) => {
  const isNZ = getRegional("code") === "NZ";
  const { locked, updateCardTempLock } = useCardTempLock(token);
  const { isLoading, mutate, isError } = updateCardTempLock;

  if (!locked) {
    return null;
  }

  if (locked === "FRAUD_LOCKED") {
    return (
      <LockUnlockUnavailable
        cardName={cardName}
        cardEndsWith={cardEndsWith}
        isFraudLocked
      />
    );
  }

  const isCardLocked = StateMap[locked];

  const UpdateErrorMessage = (
    <Message
      severity="blocking"
      bg="secondary.pink.t30"
      testId="temp-lock-switch-response-error"
    >
      We encountered an unexpected problem and are experiencing difficulties
      processing your request. Please try again or contact Latitude if the
      problem persists.
    </Message>
  );

  return (
    <Stack gap={8}>
      <Container bg="grey.t05" padding="4">
        <CreditCard cardName={cardName} cardEndsWith={cardEndsWith} />
      </Container>
      <Stack gap={8} width="100%" width-md="350">
        <LockUnlockCardImage
          isLocked={isCardLocked}
          cardName={cardName}
          imageFilename={imageFilename}
        />
        <Switch
          id="lockUnlockCard"
          checked={!!locked && isCardLocked}
          onChange={(checked) => mutate(checked)}
          unCheckedChildren=""
          checkedChildren=""
          loading={isLoading}
          labelChildren={
            <Text textStyle="heading5" color="primary.blue.t100">
              Lock card temporarily
            </Text>
          }
        />
        {isError && UpdateErrorMessage}
        <Text>
          {isCardLocked
            ? "Unlocking your card will allow all transactions to be processed again."
            : "Prevents most card, online, recurring payments and mobile wallet transactions on your primary card."}
        </Text>
      </Stack>
      <Container bg="secondary.lightBlue.t25" padding="4">
        <Container bg="white" padding="6">
          <List>
            <List.Item>
              Allows you to temporarily block most transactions on your own card
              if you have misplaced it. It won’t lock any additional
              cardholder’s card. You can unlock your card at anytime.
            </List.Item>
            <List.Item>
              The lock generally prevents most transactions, including most
              card, contactless, online, digital wallet, promotional offer and
              ATM transactions. It may also prevent some recurring, instalment
              or scheduled transactions.
            </List.Item>
            <List.Item>
              Some transactions are not blocked and other conditions apply. Full
              details are available in the Latitude{" "}
              <Link href={isNZ ? tncPdfNz : tncPdfAu} newTab>
                T&Cs
              </Link>
            </List.Item>
          </List>
          <Text margin="4 0 0 4">
            <strong>
              If you notice any suspicious transactions or are unable to quickly
              find your misplaced card, call us immediately.
            </strong>
          </Text>
        </Container>
      </Container>

      <Link
        appearance="primary-button"
        variant="blue-button"
        href="/manage-account"
        newTab={false}
      >
        Manage account
      </Link>
    </Stack>
  );
};
